import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { jobsApi, type JobListItemDto } from "@/lib/jobs";
import {
  Command,
  CommandInput,
  CommandList,
  CommandItem,
  CommandEmpty,
} from "@/components/ui/command";

export interface JobPickerProps {
  value: number | null;
  valueLabel?: string | null;
  onChange: (jobId: number | null, job: JobListItemDto | null) => void;
  placeholder?: string;
}

export function JobPicker({
  value,
  valueLabel,
  onChange,
  placeholder,
}: JobPickerProps) {
  const [q, setQ] = useState(valueLabel ?? "");

  // Sync internal search query if the external label changes
  useEffect(() => {
    if (valueLabel != null) {
      setQ(valueLabel);
    }
  }, [valueLabel]);

  const { data } = useQuery({
    queryKey: ["job-finder", q],
    queryFn: () => jobsApi.list({ q, pageSize: 10 }),
    select: (r) => r.items,
    enabled: q.length >= 2,
    staleTime: 1500,
  });

  return (
    // shouldFilter={false}: server-side search via useQuery; cmdk's built-in
    // client-side filter would hide items that don't match current input before
    // async results arrive.
    <Command shouldFilter={false} className="h-auto border border-border rounded-md mt-2">
      <CommandInput
        placeholder={placeholder ?? "Search jobs by # or client..."}
        value={q}
        onValueChange={(newQ) => {
          setQ(newQ);
          // If the user starts typing and changes the selected text, clear the selection
          if (value != null && newQ !== valueLabel) {
            onChange(null, null);
          }
        }}
      />
      {q.length > 0 && value == null && (
        <CommandList>
          {data && data.length === 0 && <CommandEmpty>No matches</CommandEmpty>}
          {data?.map((j) => (
            <CommandItem
              key={j.jobId}
              value={String(j.jobId)}
              onSelect={() => {
                setQ(j.jobReferenceNo);
                onChange(j.jobId, j);
              }}
            >
              <span>{j.jobReferenceNo}</span>
              {j.clientName && (
                <span className="ml-2 text-muted-foreground">{j.clientName}</span>
              )}
            </CommandItem>
          ))}
        </CommandList>
      )}
    </Command>
  );
}
